import React, { useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { withEdgeErrorBoundary } from 'components/common/with-edge-error-boundary'
import { WidgetFallback } from 'components/common/widget-fallback'
import { useTranslation } from 'react-i18next'
import { yAxisLabel } from 'utils/format'
import classNames from 'classnames/bind'
import styles from './CompetitorAnalysisChart.module.scss'
import { useFormatMetricValue } from 'components/common/hooks/useFormatMetricValue'
import { timePeriodFormat, isWeekend } from 'utils/helpers'
import { ReactComponent as MetricUpArrow } from 'images/performance-report/metric-up.svg'
import { ReactComponent as MetricDownArrow } from 'images/performance-report/metric-down.svg'
import { Locales } from 'utils/constants'
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Line,
    Area,
    Scatter,
    ResponsiveContainer,
    Label,
    ComposedChart,
    ReferenceArea,
    ScatterChart
  } from 'recharts'

const cx = classNames.bind(styles)

const USER_STATION_COLOR = '#39c258';

const COLOUR_PALETTE = [
  '#6509ef',
  '#0bd7ee',
  '#ff5676',
  '#a371f0',
  '#09ee42',
  '#ffacbb',
  '#f5ce0a',
  '#482779',
  '#217033',
  '#b53049',
  '#31919c'
]

const USER_STATION_IDENTIFIER = 'Your station';
const POLE_PRICE_METRIC_KEY = 'POLE_PRICE';
const USER_STATION_COMPARISON_METRIC_KEY = 'userStationComparisonMetrics';

/**
 * Gets the color for a station/competitor in the chart
 * @param {string} itemDataKey - Station identifier or data key
 * @param {string[]} [competitorList] - Array of competitor identifiers
 * @returns {string} Hex color code
 */
const getStationColor = (itemDataKey, competitorList) => {
  if (itemDataKey === USER_STATION_IDENTIFIER || itemDataKey === USER_STATION_COMPARISON_METRIC_KEY) {
    return USER_STATION_COLOR;
  }

  const index = competitorList?.findIndex(item => item === itemDataKey)
  const colourIndex = index % COLOUR_PALETTE.length
  return COLOUR_PALETTE[colourIndex]
}

/**
 * Compiles chart data for the competitor analysis chart
 * @param {Object} competitorAnalysisData - Contains ownPolePrices, competitorPolePrices, and comparisonMetric
 * @returns {Object} Compiled chart data with lineChartData, scatterChartData, minDatetime, maxDatetime, and maxRightYAxis
 */
const compileChartData = (competitorAnalysisData) => {
  const polePriceSets = [
    competitorAnalysisData?.ownPolePrices,
    ...competitorAnalysisData?.competitorPolePrices
  ];
  const comparisonMetricData = competitorAnalysisData?.comparisonMetric
  const polePriceDateTimes = 
    competitorAnalysisData?.ownPolePrices?.datetimes.map(
      (datetime) => moment(datetime)
    );

  const maxMetricValue = Math.max(
    ...comparisonMetricData.values.map(value => Number(value))
  );
  // Round up to nearest 10
  const maxRightYAxis = Math.ceil(maxMetricValue / 10) * 10;
  const minDatetime = moment.min(polePriceDateTimes)
  const maxDatetime = moment.max(polePriceDateTimes)

  const lineChartData = []
  const scatterChartData = []

  const metricDataPoints = Object.fromEntries(
    comparisonMetricData?.dates.map((date, i) => {
      return ([date, comparisonMetricData.values[i]])
    })
  );

  const metricData = {
    dataKey: USER_STATION_COMPARISON_METRIC_KEY,
    metricType: comparisonMetricData.name,
    dataPoints: metricDataPoints,
  }

  const scatterMetricData = Object.entries(metricDataPoints).slice(0, -1).map(([x,y]) => {
    return ({
      dataKey: USER_STATION_COMPARISON_METRIC_KEY,
      metricType: comparisonMetricData.name,
      x: moment(x, "YYYY-MM-DD HH:mm:ss").add(12, 'hour').valueOf(),
      yRight: parseFloat(y)
    })
  });

  lineChartData.push(metricData)
  scatterChartData.push(scatterMetricData)

  polePriceSets?.forEach((item) => {
    const dataKey = item.competitor || USER_STATION_IDENTIFIER;
    const dataPoints = Object.fromEntries(
      item.datetimes?.map((datetime, i) => [datetime, item.polePrices[i]]))
    const scatterData = Object.entries(dataPoints).map(([x,y]) => {
      return ({
      dataKey,
      metricType: POLE_PRICE_METRIC_KEY,
      x: moment(x, "YYYY-MM-DD HH:mm:ss").valueOf(),
      y: parseFloat(y)})
    })

    const lineData = {
      dataKey,
      metricType: POLE_PRICE_METRIC_KEY,
      dataPoints
    }

    lineChartData.push(lineData)
    scatterChartData.push(scatterData)
  })
  
  return {
    lineChartData, 
    scatterChartData, 
    minDatetime, 
    maxDatetime, 
    maxRightYAxis
  }
}

/**
 * Formats the line data for the competitor analysis chart
 * @param {Object[]} seriesArray - Array of line chart series with dataKey and dataPoints
 * @returns {Array<Object>} Formatted line data array. Each object has a timestamp (number) and dynamic dataKey properties (number values)
 * @returns {number} return[].timestamp - Unix timestamp in milliseconds
 * @returns {number} return[][dataKey] - Value for each series dataKey at this timestamp
 */
const formatLineData = (seriesArray) => {
  const allTimestampsSet = new Set();

  seriesArray.forEach(series => {
    Object.keys(series.dataPoints).forEach(timestamp => {
      const formatted = moment(timestamp)
      .format('YYYY-MM-DD HH:mm:ss')
      .valueOf();

      allTimestampsSet.add(formatted);
    });
  });

  const allTimestamps = Array.from(allTimestampsSet).sort((a, b) => new Date(a) - new Date(b));

  const result = allTimestamps.map(timestamp => {
    const row = { timestamp: moment(timestamp).valueOf() };

    seriesArray.forEach(series => {
      const { dataKey, dataPoints } = series;

      const matchedEntry = Object.entries(dataPoints).find(([rawTs]) =>
        moment(rawTs).format('YYYY-MM-DD HH:mm:ss') === timestamp
      );

      if (matchedEntry) {
        const [, value] = matchedEntry
        row[dataKey] = value;
      }
    });
    return row;
  });

  return result;
}

const legendFormatter = (value) => {
  // Remove user station comparison metric from legend
  if (value === USER_STATION_COMPARISON_METRIC_KEY) {
    return null;
  }

  return (
    <span style={{ color: '#b2bfca' }} iconType={'circle'}>
      {value}
    </span>
  )
}

/**
 * Creates reference area properties for weekend highlighting
 * @param {string|moment.Moment|Date} date - Date to create reference area for
 * @returns {Object} Reference area props with x1, x2 (Unix timestamps), fill, and fillOpacity
 * @property {number} x1 - Start timestamp (Unix milliseconds)
 * @property {number} x2 - End timestamp (Unix milliseconds, date + 1 day)
 * @property {string} fill - Fill color hex code
 * @property {number} fillOpacity - Fill opacity (0-1)
 */
const plotWeekendReferenceArea = (date) => {
  const formattedDate = moment(date)
  const x1 = formattedDate.format('YYYY-MM-DD HH:mm:ss')
  const x2 = formattedDate.add(1, 'day').format('YYYY-MM-DD HH:mm:ss')

  return {
    x1: moment(x1).valueOf(),
    x2: moment(x2).valueOf(),
    fill: "#f6f8fa",
    fillOpacity: 1,
  }
}

/**
 * Custom dot component for scatter chart with hover effect
 * @param {Object} props - Component props
 * @param {number} props.cx - X coordinate of the dot
 * @param {number} props.cy - Y coordinate of the dot
 * @param {string} props.color - Fill color for the dot
 * @param {ScatterDataPoint} [props.payload] - Data point payload
 * @returns {JSX.Element|null} SVG circle element or null if payload is invalid
 */
const CustomDot = ({cx, cy, color, payload}) => {
  const [hovered, setHovered] = useState(false)

  if (!payload || payload.y === null || Number.isNaN(payload.y)) { 
    return null
  }

  const radius = hovered ? 5 : 2
  return (
    <circle 
      cx={cx} 
      cy={cy}
      fill={color}
      r={radius}
      stroke="transparent"
      strokeWidth={10}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    />
  )
}

const getItemLabel = (item, metricName, t) => {
  let label = `${item.payload.dataKey} pole price`;

  if (item.payload.dataKey === USER_STATION_IDENTIFIER) {
    label = 'Your pole price'
  } else if (item.payload.dataKey === USER_STATION_COMPARISON_METRIC_KEY) {
    label = `Your ${(t(`widgets.analytics.yAxisLabels.${yAxisLabel(metricName)}`)).toLowerCase()}`
  }

  return label
}

/**
 * Calculates the difference between current and previous datapoint to be displayed in the tooltip
 * @param {Object[][]} chartData - Array of scatter data point arrays
 * @param {Object} payload - Current data point with dataKey and x properties
 * @returns {Object|null} Object containing tooltipDifferenceValue, or null if first/last item
 * @returns {number} return.tooltipDifferenceValue - Percentage difference ((current - previous) / previous * 100) for comparison metric or difference value (current - previous) for pole price 
 */
const getTooltipDiff = (chartData, payload) =>  {
  const [matchedDataKeyArray] = chartData.filter(arr => arr.some(datapoint => datapoint.dataKey === payload.dataKey))
  const currentItemIndex = matchedDataKeyArray.findIndex(item => item.x === payload.x)

  // Don't compute first or last  
  if (currentItemIndex <= 0 || payload.dataKey !== USER_STATION_COMPARISON_METRIC_KEY && currentItemIndex === Object.values(matchedDataKeyArray).length - 1) {
    return null;
  }
  
  const yValueRef = payload.dataKey === USER_STATION_COMPARISON_METRIC_KEY ? 'yRight' : 'y';
  const dataPoints = matchedDataKeyArray.map(data => data[yValueRef]);
  const currentValue = Number(dataPoints[currentItemIndex]);
  const prevValue = Number(dataPoints[currentItemIndex - 1]);

  const tooltipDifferenceValue = () => {
    if (currentValue === 0 && prevValue === 0) {
      return 0
    } else if (prevValue === 0) {
      return 100
    } else {
      return payload.dataKey === USER_STATION_COMPARISON_METRIC_KEY ? 
        Number((currentValue - prevValue) / prevValue * 100) : 
        Number(currentValue - prevValue)
    }
  }

  return tooltipDifferenceValue()
};

const CompetitorAnalysisChart = ({ data, metricType, metricName }) => {
  const { t, i18n: { language } } = useTranslation(['translations'])
  const { formatMetricValue } = useFormatMetricValue()

  const compiledChartData = compileChartData(data?.competitorAnalysis)
  const competitorDataKeys = compiledChartData.scatterChartData.map(
    arr => arr[0].dataKey).filter(item => item !== USER_STATION_IDENTIFIER && item !== USER_STATION_COMPARISON_METRIC_KEY
  )

  const weekendDates = data?.competitorAnalysis?.comparisonMetric?.dates.filter((date) => isWeekend(date) === true)
  
  const formattedLineData = formatLineData(compiledChartData.lineChartData)

  const customDailyTicks = (minTs, maxTs) => {
    const timestamps = []

    let current = minTs.clone()
    while (current.isSameOrBefore(maxTs)) {
      timestamps.push(current.valueOf())
      current.add(1, "day")
    }
    return timestamps
  }

  const LeftYAxisTick = ({ x, y, payload }) => { 
    return (
      <g transform={`translate(${x},${y})`}>
        <text textAnchor="end" fill="#b2bfca" >
          {formatMetricValue('CHART_AXIS_POLE_PRICE', payload.value, true, true)}
        </text>
      </g>
    )
  }

  const RightYAxisTick = ({ x, y, payload }) => {   
    return (
      <g transform={`translate(${x},${y})`}>
        <text fill="#b2bfca" >
          {formatMetricValue(metricType, payload.value, true, true)}
        </text>
      </g>
    )
  }

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) {
      return null;
    }

    const [xAxisPayload] = payload;

    const unixTimestamp = xAxisPayload.payload.x
    const datetime = moment(unixTimestamp).format("YYYY-MM-DD HH:mm:ss")

    const formattedTimestamp = language === Locales.EN_US
      ? moment(unixTimestamp).format('h:mma')
      : moment(unixTimestamp).format('HH:mm')

    const dayOfWeek = moment(datetime).format("dddd")
    const dateLabel = `${dayOfWeek} ${timePeriodFormat(datetime, dates)}`
    const dateTimeLabel = `${dayOfWeek} ${timePeriodFormat(datetime, dates)} ${formattedTimestamp}`
    
    const [filteredPayload] = payload.filter(item => item.name === 'y' || item.name === 'yRight')

    const tooltipDifferenceValue = getTooltipDiff(
      compiledChartData.scatterChartData, 
      xAxisPayload.payload
    )

    const comparisonMetricDate = 
      <div>
        <div className={cx('tooltip-date')}>
          <p>{dateLabel}</p>
        </div>  
        {tooltipDifferenceValue > 0 &&
        <div className={cx('metric-change-up')}>
          <p>
            <MetricUpArrow />
          </p>
          <span>
            {tooltipDifferenceValue.toFixed(2) + '%'} {t('widgets.analytics.comparedToPreviousDay')}
          </span>
        </div>
        }
        {tooltipDifferenceValue < 0 &&
        <div className={cx('metric-change-down')}>
          <p>
            <MetricDownArrow/>
          </p>
          <span>
            {(-tooltipDifferenceValue.toFixed(2) + '%')} {t('widgets.analytics.comparedToPreviousDay')}</span>
        </div>}
        {tooltipDifferenceValue === 0 &&
        <div className={cx('metric-no-change')}>
          <span>
            {t('widgets.analytics.noChangeFromPreviousDay')}</span>
        </div>}
      </div>

    const polePriceDatetime = 
      <div>
        <div className={cx('tooltip-date')}>
          <p>{dateTimeLabel}</p>
        </div>  
        {tooltipDifferenceValue > 0 &&
        <div className={cx('metric-change-up')}>
          <p>
            <MetricUpArrow/>
          </p>
          <span>
          {formatMetricValue(POLE_PRICE_METRIC_KEY, tooltipDifferenceValue, true)} {t('widgets.analytics.fromPreviousPolePrice')}
          </span>
        </div>
        }
        {tooltipDifferenceValue < 0 &&
        <div className={cx('metric-change-down')}>
          <p>
            <MetricDownArrow/>
          </p>
          <span>
          {formatMetricValue(POLE_PRICE_METRIC_KEY, -tooltipDifferenceValue, true)} {t('widgets.analytics.fromPreviousPolePrice')}
            </span>
        </div>}
        {tooltipDifferenceValue === 0 &&
        <div className={cx('metric-no-change')}>
          <span>
            {t('widgets.analytics.noChangeFromPreviousPolePrice')}</span>
        </div>}
      </div>

    return (
      <div className={cx('tooltip')}>
      {filteredPayload.name === 'yRight' ? comparisonMetricDate : polePriceDatetime}
      <table className={cx('tooltip-periods')}>
        <thead>
          <tr>
            <th>{(getItemLabel(filteredPayload, metricName, t))}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td 
              key={filteredPayload.payload.dataKey} 
              style={{ color: getStationColor(filteredPayload.payload.dataKey, competitorDataKeys) }}
            >
              {formatMetricValue(
                filteredPayload.dataKey === 'yRight' ? metricType : POLE_PRICE_METRIC_KEY, filteredPayload.value, true
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    )
  }

  const maxRightYAxis = Math.max(compiledChartData.maxRightYAxis, 10);
  const startDatetime = compiledChartData.minDatetime
  const endDatetime = compiledChartData.maxDatetime
  const dates = customDailyTicks(startDatetime, endDatetime).map(
    (ts) => moment(ts).format('YYYY-MM-DD')
  );

  // We're layering two charts on top of each other, one for the line chart and one for the scatter chart. This is so we can combine the tooltip functionality of the scatter chart while retaining the line chart appearance. 
  return (
    <div className={cx('container')} >
      <ResponsiveContainer className={cx('chart-container')}>
        <ComposedChart 
          className={cx('chart')}
          data={formattedLineData}
          margin={{ left: 48, right: 48 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            className={cx('grid')} 
            vertical={true} 
          />
          <XAxis
            dataKey="timestamp"
            type='number'
            domain={[startDatetime, endDatetime]}
            ticks={customDailyTicks(startDatetime, endDatetime)}
            tick={{ fill: "#b2bfca" }}
            tickFormatter={(value) => 
               timePeriodFormat(moment(moment(value).valueOf()).format('YYYY-MM-DD'), [startDatetime, endDatetime])
            }
            stroke="#eee"
            fontSize={13}
          />
          <YAxis 
            yAxisId="right" 
            orientation="right"
            domain={[0, maxRightYAxis]}
            padding="10px"
            tick={<RightYAxisTick />}
            stroke="none">
            <Label 
              value={t(`widgets.analytics.yAxisLabels.${yAxisLabel(metricName)}`)}
              angle={-90}
              position="insideRight"
              style={{ textAnchor: 'middle', fontSize: '15px'}}
              dx={28}
            />
          </YAxis>
          <YAxis tick={<LeftYAxisTick />} stroke="none" domain={['auto']}>
            <Label
              value={t(`widgets.analytics.yAxisLabels.${yAxisLabel(POLE_PRICE_METRIC_KEY.toLowerCase())}`)}
              angle={-90}
              position="insideLeft"
              style={{ textAnchor: 'middle', fontSize: '15px' }}
              dx={-47}
            />
          </YAxis>
          {weekendDates.map((date) => (
            <ReferenceArea 
              {...plotWeekendReferenceArea(date)}
            />
          ))} 
          {compiledChartData.lineChartData.filter((series) => series.dataKey !== USER_STATION_COMPARISON_METRIC_KEY).map((series) => (
            <Line
              connectNulls
              key={series.dataKey}
              legendType='circle'
              type='stepAfter'
              dataKey={series.dataKey}
              stroke={getStationColor(series.dataKey, competitorDataKeys)}
              strokeWidth={2}
              dot={false}
            />
          ))}
          <Area 
            connectNulls
            yAxisId="right"
            type="stepAfter"
            dataKey="userStationComparisonMetrics"
            stroke="#39c258"
            fill="#39c258"
            strokeWidth={0}
            fillOpacity={0.1}
            legendType='none'
          />
          <Legend
            className={cx('legend')}
            formatter={legendFormatter} 
            wrapperStyle={{ height: '49px', marginTop: '25px', whiteSpace: 'nowrap', overflowX: 'scroll', overflowY: 'hidden' }}
          />          
        </ComposedChart>
      </ResponsiveContainer>
      <ResponsiveContainer className={cx('chart-container')} height={551}>
        <ScatterChart
          className={cx('chart')}
          margin={{ left: 48, right: 48 }}
          style={{ backgroundColor: 'transparent' }}
        >
          <XAxis
            dataKey="x"
            type="number"
            domain={[startDatetime, endDatetime]}
            ticks={customDailyTicks(startDatetime, endDatetime)}
            tick={{ fill: "#b2bfca" }}
            tickFormatter={(value) => 
              timePeriodFormat(moment(value).format('YYYY-MM-DD'),[startDatetime, endDatetime])
            }
            stroke="#eee"
            fontSize={13}
          />
          <YAxis 
            yAxisId="left"
            dataKey="y" 
            domain={['auto']}
            tick={<LeftYAxisTick />}
            stroke="none"
            >
            <Label
              value={t(`widgets.analytics.yAxisLabels.${yAxisLabel(POLE_PRICE_METRIC_KEY.toLowerCase())}`)}
              angle={-90}
              position="insideLeft"
              style={{ textAnchor: 'middle', fontSize: '15px' }}
              dx={-47}
            />
          </YAxis> 
          <YAxis 
            yAxisId="right" 
            dataKey="yRight"
            domain={[0, maxRightYAxis]}
            orientation="right" 
            padding="10px"
            tick={<RightYAxisTick />}
            stroke="none">
            <Label 
              value={t(`widgets.analytics.yAxisLabels.${yAxisLabel(metricName)}`)}
              angle={-90}
              position="insideRight"
              style={{ textAnchor: 'middle', fontSize: '15px'}}
              dx={28}
            />
          </YAxis>
          <Tooltip wrapperStyle={{ outline: "none" }} content={<CustomTooltip metricType={POLE_PRICE_METRIC_KEY}/>} />
          {compiledChartData.scatterChartData
            .map((series) => {
              if (!series.length) {
                return null;
              }
              
              return (
                <Scatter
                  data={series}
                  yAxisId={series[0].dataKey === USER_STATION_COMPARISON_METRIC_KEY ? "right" : "left"}
                  shape={(props) => <CustomDot {...props} color ={getStationColor(series[0].dataKey, competitorDataKeys)} />}
                />
              )
          })}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  )}

const FallbackComponent = () => <WidgetFallback />

const ChartWithErrorBoundary = withEdgeErrorBoundary(
  CompetitorAnalysisChart,
  FallbackComponent,
  'CompetitorAnalysisChart'
)

CompetitorAnalysisChart.propTypes = {
  metricType: PropTypes.string,
  metricName: PropTypes.string,
  timeResolution: PropTypes.string,
  data: PropTypes.shape({
    competitorAnalysis: PropTypes.shape()
  })
}

export {
  compileChartData,
  formatLineData,
  getStationColor,
  getItemLabel,
  getTooltipDiff,
  plotWeekendReferenceArea,
  ChartWithErrorBoundary as CompetitorAnalysisChart 
}
